import React from 'react'
import { useFormikContext } from 'formik'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
  Box,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Backdrop,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Radio,
  RadioGroup,
  Paper,
} from '@mui/material'
import GpsFixedIcon from '@mui/icons-material/GpsFixed'
import PlaceIcon from '@mui/icons-material/Place'
import HikingIcon from '@mui/icons-material/Hiking'
import LocationCityIcon from '@mui/icons-material/LocationCity'
import BusinessIcon from '@mui/icons-material/Business'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import CheckIcon from '@mui/icons-material/Check'
import VisibilityIcon from '@mui/icons-material/Visibility'
import NatureIcon from '@mui/icons-material/Nature'
import QrCodeIcon from '@mui/icons-material/QrCode'
import ItemsContainer from '../../ItemsContainer'
import SliderField from '../components/SliderField'
import CollapsibleAdvanced from '../components/CollapsibleAdvanced'
import PlusCodePreview from '../components/PlusCodePreview'
import { fetchElevation } from '../../../services/elevationService'
import { useMapContext } from '../../../context/MapContext'
import { useCities } from '../../../context/CitiesContext'
import { useAuth } from '../../../context/AuthProvider'
import { useTreePermissions } from '../../../hooks/useTreePermissions'
import { TREE_PERMISSION } from '../../../utils/treePermissions'
import { detectCityFromURL } from '../../../utils/cityDetector'
import { buildCantonUrl } from '../../../utils/cantonUrl'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import RestoreIcon from '@mui/icons-material/Restore'
import { setAddMode, setTreeCoords, setMapExpanded } from '../../../redux/slices/Map/map'
import { setEnableSectionField } from '../../../redux/slices/Tree/treeFeatures'
import { getSitesDataByName, getGoogleCodePreviewByCoords, activateTenant } from '../../../services'
import * as RT from '../../../config/routeType'

const pickItems = (items, names) =>
  names.map(name => items.find(i => i.name === name)).filter(Boolean)

const readSelectedText = value => {
  if (value == null) return ''
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value).trim()
  }
  if (typeof value === 'object') {
    return String(value.label ?? value.id ?? '').trim()
  }
  return ''
}

const SectionHeader = ({ icon: Icon, label }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
    <Icon sx={{ fontSize: 20, color: 'primary.main' }} />
    <Typography variant="subtitle2" color="primary" fontWeight={600}>
      {label}
    </Typography>
  </Stack>
)

const LocationStep = ({
  itemsGeneral,
  itemsAttributes,
  mapComponent,
  isMobile,
  zoneTypesData,
  allSiteTypes,
  siteTypesData,
  editMode = false,
  treeData = null,
  existingCode,
  existingCodeMode,
  existingGoogleCode,
  existingGoogleCodeSuffix,
  coordinatesOutOfTenantBounds = false,
}) => {
  const { values, setFieldValue } = useFormikContext()
  const { map } = useMapContext()
  const { coords, addMode, mapExpanded } = useSelector(state => state.mapState)
  const { drawerHeight, drawerMinHeight } = useSelector(state => state.drawerLeft)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const { getEnabledCities, getTenantsByCantonSlug, selectedTenant, selectTenant, getEnabledCantones } =
    useCities()
  const cantones = getEnabledCantones()
  const { allowedCantonSlugs } = useAuth()
  const { getPermittedTenants } = useTreePermissions()
  const currentCity = detectCityFromURL(location.pathname) || 'loja'

  // Permiso del modo: registrar al crear, actualizar al editar/reasignar.
  const tenantPermission = editMode ? TREE_PERMISSION.UPDATE : TREE_PERMISSION.REGISTER

  // Solo cantones con algún tenant en el que el modo está permitido: el guard de la ruta exige lo mismo.
  const cities = React.useMemo(() => {
    const all = getEnabledCities()
    if (!allowedCantonSlugs) return all
    return all.filter(
      c =>
        allowedCantonSlugs.includes(c.slug) &&
        getPermittedTenants(getTenantsByCantonSlug(c.slug), tenantPermission).length > 0,
    )
  }, [getEnabledCities, allowedCantonSlugs, getPermittedTenants, getTenantsByCantonSlug, tenantPermission])

  const [formCantonSlug, setFormCantonSlug] = React.useState(currentCity)
  // In edit mode, use the locally chosen canton so tenants update without navigation
  const activeCantonSlug = editMode ? formCantonSlug : currentCity

  // Initialize canton + tenant from the tree's actual tenant ID (edit mode only).
  // Runs once cities data is available; handles trees registered under a different canton.
  const hasInitializedFromTreeData = React.useRef(false)
  const [originalCantonSlug, setOriginalCantonSlug] = React.useState(null)
  const [originalTenantSlug, setOriginalTenantSlug] = React.useState(null)
  React.useEffect(() => {
    if (!editMode || !treeData?.tenantId || hasInitializedFromTreeData.current) return
    if (!cities.length) return // wait for cities context to load
    for (const city of cities) {
      const cityTenants = getTenantsByCantonSlug(city.slug)
      const matchedTenant = cityTenants.find(t => String(t.id) === String(treeData.tenantId))
      if (matchedTenant) {
        hasInitializedFromTreeData.current = true
        setFormCantonSlug(city.slug)
        setOriginalCantonSlug(city.slug)
        setOriginalTenantSlug(matchedTenant.slug)
        selectTenant(matchedTenant.slug)
        break
      }
    }
  }, [editMode, treeData?.tenantId, cities, getTenantsByCantonSlug, selectTenant])

  // Solo los tenants del cantón con el permiso del modo. Admin ve todos (incluido ÁrbolEc genérico);
  // el genérico solo si está asignado con ese permiso.
  const tenants = React.useMemo(
    () => getPermittedTenants(getTenantsByCantonSlug(activeCantonSlug), tenantPermission),
    [getPermittedTenants, getTenantsByCantonSlug, activeCantonSlug, tenantPermission],
  )

  const currentCityName = React.useMemo(
    () => cities.find(c => c.slug === activeCantonSlug)?.name || activeCantonSlug,
    [cities, activeCantonSlug],
  )
  const selectedTenantName = React.useMemo(
    () => (selectedTenant ? tenants.find(t => t.slug === selectedTenant)?.name : null),
    [selectedTenant, tenants],
  )

  const selectedTenantIsGeneric = React.useMemo(
    () => (selectedTenant ? tenants.find(t => t.slug === selectedTenant)?.isGeneric || false : false),
    [selectedTenant, tenants],
  )

  // Original canton/tenant display names (edit mode — set once on initialization)
  const originalCantonName = React.useMemo(
    () => (originalCantonSlug ? cities.find(c => c.slug === originalCantonSlug)?.name || originalCantonSlug : null),
    [cities, originalCantonSlug],
  )
  const originalTenantName = React.useMemo(() => {
    if (!originalCantonSlug || !originalTenantSlug) return null
    const allTenantsForOriginal = getTenantsByCantonSlug(originalCantonSlug)
    return allTenantsForOriginal.find(t => t.slug === originalTenantSlug)?.name || originalTenantSlug
  }, [originalCantonSlug, originalTenantSlug, getTenantsByCantonSlug])

  const cantonChanged = editMode && originalCantonSlug != null && formCantonSlug !== originalCantonSlug
  const tenantChanged = editMode && originalTenantSlug != null && selectedTenant !== originalTenantSlug

  const [isChangingAssignment, setIsChangingAssignment] = React.useState(false)

  const handleCancelAssignmentChange = () => {
    setIsChangingAssignment(false)
    if (originalCantonSlug) {
      setFormCantonSlug(originalCantonSlug)
    }
    if (originalTenantSlug) {
      selectTenant(originalTenantSlug)
    }
    dispatch(setAddMode(false))
  }

  const [cityAnchor, setCityAnchor] = React.useState(null)
  const [tenantAnchor, setTenantAnchor] = React.useState(null)

  // Activar tenant en JWT cada vez que cambia la selección (incluye montaje con tenant ya seleccionado)
  // lastActivatedTenantIdRef evita llamadas duplicadas cuando el mismo tenant ya fue activado
  const lastActivatedTenantIdRef = React.useRef(null)
  React.useEffect(() => {
    if (!selectedTenant) return
    const tenant = tenants.find(t => t.slug === selectedTenant)
    if (!tenant?.id) return
    if (lastActivatedTenantIdRef.current === tenant.id) return
    lastActivatedTenantIdRef.current = tenant.id
    const token = window.localStorage.getItem('ArbolEc_token')
    activateTenant(tenant.id, token).then(result => {
      if (result?.token) window.localStorage.setItem('ArbolEc_token', result.token)
    })
  }, [selectedTenant, tenants])

  const handleCityChange = slug => {
    setCityAnchor(null)
    if (slug === activeCantonSlug) return

    if (editMode) {
      // In edit mode: update the target canton locally — no navigation, tree data preserved
      setFormCantonSlug(slug)
      selectTenant(null)
      dispatch(setAddMode(false))
      return
    }

    dispatch(setAddMode(false))
    dispatch(setTreeCoords(null))
    navigate(buildCantonUrl(slug, cantones, `/${RT.REGISTER_TREE}`))
  }

  // En edit mode, si el researcher selecciona un tenant fuera de su lista asignada
  const tenantAccessDenied = React.useMemo(() => {
    if (!selectedTenant || !allowedCantonSlugs) return false
    return tenants.length > 0 && !tenants.some(t => t.slug === selectedTenant)
  }, [selectedTenant, allowedCantonSlugs, tenants])

  // Auto-abrir reasignación cuando el tenant actual no es accesible para este usuario.
  // Solo aplica en edit mode, después de que la inicialización haya encontrado el cantón original.
  React.useEffect(() => {
    if (tenantAccessDenied && originalCantonSlug && !isChangingAssignment) {
      setIsChangingAssignment(true)
    }
  }, [tenantAccessDenied, originalCantonSlug]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleTenantChange = slug => {
    setTenantAnchor(null)
    selectTenant(slug || null)
    dispatch(setAddMode(false))

    // En edit mode conservar coordenadas existentes para que el validador de límites
    // las evalúe contra el nuevo tenant (muestra "fuera de límites" si aplica)
    const hasExistingCoords =
      editMode &&
      values.longX && values.latY &&
      parseFloat(values.longX) !== 0 && parseFloat(values.latY) !== 0

    if (!hasExistingCoords) {
      dispatch(setTreeCoords(null))
      // Clear coordinates — they belong to the previous tenant's territory
      setFieldValue('longX', '')
      setFieldValue('latY', '')
      setFieldValue('altitud', 0)
      setElevationAutoFetched(false)
      setManualOverride(false)
      setElevationError(false)
      lastFetchedCoords.current = null
    }

    if (slug) {
      const tenant = tenants.find(t => t.slug === slug)
      if (map && tenant?.center && !hasExistingCoords) {
        map.flyTo({ center: tenant.center, zoom: tenant.zoom || 15, essential: true })
      }
      // El useEffect detecta el cambio de selectedTenant y llama activateTenant automáticamente
    }
  }

  const [elevationLoading, setElevationLoading] = React.useState(false)
  const [elevationAutoFetched, setElevationAutoFetched] = React.useState(false)
  const [elevationError, setElevationError] = React.useState(false)
  const [manualOverride, setManualOverride] = React.useState(false)
  const [recalculateRequested, setRecalculateRequested] = React.useState(false)
  const [registerGoogleCodePreview, setRegisterGoogleCodePreview] = React.useState(null)
  const [registerGoogleCodeLoading, setRegisterGoogleCodeLoading] = React.useState(false)
  const [registerGoogleCodeError, setRegisterGoogleCodeError] = React.useState(false)
  const lastFetchedCoords = React.useRef(null)
  const originalAltitude = React.useRef(editMode ? Number(values.altitud) || null : null)
  const mapBoxRef = React.useRef(null)

  const usePlusCode =
    values.modoCodigoPlusCode !== false && values.modoCodigoPlusCode !== 0

  const gpsCodePreview = React.useMemo(() => {
    const rawGoogleCode = String(existingGoogleCode || '').trim()
    if (!rawGoogleCode) return null
    const cleaned = rawGoogleCode
      .replace(/^gps[\s:.-]*/i, '')
      .replace(/^plus\s*code[\s:.-]*/i, '')
      .trim()
    return cleaned || null
  }, [existingGoogleCode])

  const existingCodeResolved = React.useMemo(() => {
    const directCode = String(existingCode || '').trim()
    if (directCode) return directCode
    return gpsCodePreview || ''
  }, [existingCode, gpsCodePreview])

  // ── Code change mode (edit mode) ──
  // 'keep'               → no cambiar el código actual
  // 'update_structured'  → regenerar código estructurado desde los datos del formulario
  // 'plus_code'          → cambiar a código GPS (Plus Code)
  const hasExistingCode = Boolean(existingCodeResolved)
  const defaultSelectableCodeMode =
    existingCodeMode === 'PLUS_CODE'
      ? 'plus_code'
      : values.modoCodigoPlusCode
        ? 'plus_code'
        : 'update_structured'
  const showKeepManualOption = hasExistingCode && existingCodeMode === 'MANUAL'

  const [codeChangeMode, setCodeChangeMode] = React.useState(() => {
    if (editMode && hasExistingCode) {
      if (existingCodeMode === 'MANUAL') return 'keep'
      if (existingCodeMode === 'PLUS_CODE') return 'plus_code'
      if (existingCodeMode === 'STRUCTURED') return 'update_structured'
      if (existingCodeMode === 'STRUCTURED_LEGACY') return 'update_structured'
      return defaultSelectableCodeMode
    }
    return defaultSelectableCodeMode
  })

  // Keep option is valid only for MANUAL codes.
  React.useEffect(() => {
    if (!editMode) return
    if (showKeepManualOption || codeChangeMode !== 'keep') return
    setCodeChangeMode(defaultSelectableCodeMode)
  }, [editMode, showKeepManualOption, codeChangeMode, defaultSelectableCodeMode])

  // Sync codeChangeMode → formik fields in edit mode
  React.useEffect(() => {
    if (!editMode) return
    if (codeChangeMode === 'plus_code') {
      setFieldValue('modoCodigoPlusCode', true)
      setFieldValue('codeAction', 'use_plus_code')
    } else if (codeChangeMode === 'update_structured') {
      setFieldValue('modoCodigoPlusCode', false)
      setFieldValue('codeAction', 'regenerate_structured')
    } else {
      setFieldValue('modoCodigoPlusCode', false)
      setFieldValue('codeAction', 'keep')
    }
  }, [codeChangeMode, editMode, existingCodeMode]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Section chip selector state ──
  const siteSelectedRedux = useSelector(
    state => state.treeFeatures.enableSaveButton.siteField?.selected,
  )
  const siteAcronymSelectedRedux = useSelector(
    state => state.treeFeatures.enableSaveButton.siteAcronym?.selected,
  )
  const sectionSelectedRedux = useSelector(
    state => state.treeFeatures.enableSaveButton.sectionField?.selected,
  )
  const siteSelected = siteSelectedRedux || values.site_
  // existingSections: [{ codigo: 'S1', nombre: 'General' }, ...]
  const [existingSections, setExistingSections] = React.useState([])
  const [addNewSection, setAddNewSection] = React.useState(false)
  const [showSectionName, setShowSectionName] = React.useState(false)

  const sectionCodes = React.useMemo(
    () => existingSections.map(s => s.codigo),
    [existingSections],
  )

  const nextSection = React.useMemo(() => {
    if (!sectionCodes.length) return 'S1'
    const nums = sectionCodes
      .map(s => parseInt(s.replace('S', ''), 10))
      .filter(n => !isNaN(n))
    return `S${nums.length ? Math.max(...nums) + 1 : 1}`
  }, [sectionCodes])

  const selectedSectionName = React.useMemo(() => {
    if (addNewSection) return null
    const found = existingSections.find(s => s.codigo === values.section_)
    return found?.nombre && found.nombre !== found.codigo ? found.nombre : null
  }, [existingSections, values.section_, addNewSection])

  // Fetch existing sections when site changes
  React.useEffect(() => {
    if (!siteSelected) {
      setExistingSections([])
      if (!values.section_) {
        setAddNewSection(false)
        setShowSectionName(false)
        setFieldValue('section_', 'S1')
        if (!values.sectionName_) setFieldValue('sectionName_', '')
        dispatch(setEnableSectionField({ error: false, selected: 'S1' }))
      }
      return
    }
    getSitesDataByName(siteSelected, 'seccion')
      .then(options => {
        if (!Array.isArray(options)) {
          setExistingSections([])
          return
        }
        const sections = options
          .filter(o => o.label && typeof o.label === 'string')
          .map(o => ({ codigo: String(o.label), nombre: String(o.nombre || o.label) }))
          .sort((a, b) => {
            const na = parseInt(a.codigo.replace('S', ''), 10)
            const nb = parseInt(b.codigo.replace('S', ''), 10)
            return na - nb
          })
        setExistingSections(sections)
        const codes = sections.map(s => s.codigo)
        const defaultSection = codes.includes('S1') ? 'S1' : codes[0] || 'S1'
        const currentSection = values.section_ || ''
        const nextSectionValue = currentSection || defaultSection
        if (!currentSection) {
          setAddNewSection(false)
          setShowSectionName(false)
          setFieldValue('sectionName_', '')
        }
        setFieldValue('section_', nextSectionValue)
        dispatch(setEnableSectionField({ error: false, selected: nextSectionValue }))
      })
      .catch(() => {
        setExistingSections([])
        if (!values.section_) {
          setShowSectionName(false)
          setFieldValue('section_', 'S1')
          if (!values.sectionName_) setFieldValue('sectionName_', '')
          dispatch(setEnableSectionField({ error: false, selected: 'S1' }))
        }
      })
  }, [siteSelected]) // eslint-disable-line react-hooks/exhaustive-deps

  // When "nueva sección" toggle changes, set value to nextSection or revert
  React.useEffect(() => {
    if (addNewSection) {
      setFieldValue('section_', nextSection)
      dispatch(setEnableSectionField({ error: false, selected: nextSection }))
    } else {
      setShowSectionName(false)
      setFieldValue('sectionName_', '')
      if (sectionCodes.length) {
        const fallback = sectionCodes.includes('S1') ? 'S1' : sectionCodes[0]
        if (values.section_ === nextSection) {
          setFieldValue('section_', fallback)
          dispatch(setEnableSectionField({ error: false, selected: fallback }))
        }
      }
    }
  }, [addNewSection]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSectionSelect = code => {
    setAddNewSection(false)
    setFieldValue('section_', code)
    dispatch(setEnableSectionField({ error: false, selected: code }))
  }

  // Collapse map when coordinates are captured or capture is cancelled
  React.useEffect(() => {
    if (coords && mapExpanded) {
      dispatch(setMapExpanded(false))
    }
  }, [coords]) // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!addMode && mapExpanded) {
      dispatch(setMapExpanded(false))
    }
  }, [addMode]) // eslint-disable-line react-hooks/exhaustive-deps

  // Resize map and (only in register mode) activate location capture when expanding
  React.useEffect(() => {
    if (mapExpanded && !editMode) {
      dispatch(setAddMode(true))
    }
    if (map) {
      const timer = setTimeout(() => {
        try {
          map.resize()
        } catch (e) {
          /* ignore */
        }
      }, 350)
      return () => clearTimeout(timer)
    }
  }, [mapExpanded, map, dispatch, editMode])

  const handlePreviewLocation = () => {
    const lat = parseFloat(values.latY)
    const lng = parseFloat(values.longX)
    if (isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0) return
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return
    dispatch(setTreeCoords({ lng, lat }))
    if (map) {
      map.flyTo({
        center: [lng, lat],
        zoom: Math.max(map.getZoom(), 17),
        essential: true,
      })
    }
  }

  const hasValidManualCoords = (() => {
    const lat = parseFloat(values.latY)
    const lng = parseFloat(values.longX)
    return (
      !isNaN(lat) &&
      !isNaN(lng) &&
      lat !== 0 &&
      lng !== 0 &&
      lat >= -90 &&
      lat <= 90 &&
      lng >= -180 &&
      lng <= 180
    )
  })()

  // ── Pick items from formik definitions ──
  const coordItems = pickItems(itemsAttributes, ['longX', 'latY'])
  const zoneTypeItems = pickItems(itemsGeneral, ['typeSite_'])
  const siteItems = pickItems(itemsGeneral, ['site_'])
  const acronymItems = pickItems(itemsGeneral, ['siteAcronym_'])

  // ── Dynamic site type filtering based on selected zone type ──
  const filteredSitePlaceItem = React.useMemo(() => {
    const baseItem = itemsGeneral.find(i => i.name === 'sitePlace_')
    if (!baseItem) return null

    const selectedZoneId = values.typeSite_
    const zoneData = zoneTypesData?.[selectedZoneId]

    // If zone has compatible site types, filter to only those
    if (zoneData?.sitiosCompatibles?.length) {
      const compatibleOptions = {}
      zoneData.sitiosCompatibles.forEach(st => {
        compatibleOptions[st.id] = st.nombre
      })
      return { ...baseItem, options: compatibleOptions }
    }

    // If "Sin clasificar" or no compatibility data, show all
    return { ...baseItem, options: allSiteTypes }
  }, [itemsGeneral, values.typeSite_, zoneTypesData, allSiteTypes])

  // Reset sitePlace_ when the user actively changes zone type and the current
  // selection is no longer compatible. Skips the initial mount to avoid wiping
  // the pre-loaded value in edit mode.
  const isFirstRenderRef = React.useRef(true)
  React.useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false
      return
    }
    if (!filteredSitePlaceItem || !values.sitePlace_) return
    const currentSiteType = values.sitePlace_
    if (currentSiteType && !filteredSitePlaceItem.options[currentSiteType]) {
      setFieldValue('sitePlace_', '')
    }
  }, [values.typeSite_]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Zone type color indicator ──
  const selectedZoneColor = zoneTypesData?.[values.typeSite_]?.color || null
  const selectedZoneCategory = zoneTypesData?.[values.typeSite_]?.categoria || null

  const resolvedAcronym = React.useMemo(() => {
    const fromRedux = readSelectedText(siteAcronymSelectedRedux)
    const fromFormik = readSelectedText(values.siteAcronym_)
    return (fromRedux || fromFormik).toUpperCase().slice(0, 4)
  }, [siteAcronymSelectedRedux, values.siteAcronym_])

  const resolvedSection = React.useMemo(() => {
    const fromRedux = readSelectedText(sectionSelectedRedux)
    const fromFormik = readSelectedText(values.section_)
    return fromRedux || fromFormik || ''
  }, [sectionSelectedRedux, values.section_])

  const structuredCodeStatus = React.useMemo(() => {
    const zPrefix = zoneTypesData?.[values.typeSite_]?.prefijo || null
    const sPrefix = siteTypesData?.[values.sitePlace_]?.prefijo || null
    const sectionForCode = resolvedSection || 'S1'
    const missing = {
      zoneType: !zPrefix,
      sitePlace: !sPrefix,
      acronym: !resolvedAcronym,
      section: !sectionForCode,
    }
    return { zPrefix, sPrefix, sectionForCode, missing }
  }, [
    values.typeSite_,
    values.sitePlace_,
    resolvedAcronym,
    resolvedSection,
    zoneTypesData,
    siteTypesData,
  ])

  // ── Code preview for structured mode ──
  const codePreview = React.useMemo(() => {
    if (usePlusCode) return null
    const { zPrefix, sPrefix, sectionForCode } = structuredCodeStatus
    if (zPrefix && sPrefix && resolvedAcronym) {
      return `${zPrefix}.${sPrefix}.${resolvedAcronym}.${sectionForCode}.?`
    }
    return null
  }, [usePlusCode, resolvedAcronym, structuredCodeStatus])

  const hasValidCoordsForGooglePreview = React.useMemo(() => {
    const lat = parseFloat(values.latY)
    const lng = parseFloat(values.longX)
    return (
      !Number.isNaN(lat) &&
      !Number.isNaN(lng) &&
      lat !== 0 &&
      lng !== 0 &&
      lat >= -90 &&
      lat <= 90 &&
      lng >= -180 &&
      lng <= 180
    )
  }, [values.latY, values.longX])

  const plusCodeOptionPreview = React.useMemo(() => {
    if (registerGoogleCodePreview) return registerGoogleCodePreview
    if (hasValidCoordsForGooglePreview) return null
    if (gpsCodePreview) return gpsCodePreview
    if (existingCodeMode === 'PLUS_CODE' && existingCodeResolved) return existingCodeResolved
    return null
  }, [
    registerGoogleCodePreview,
    hasValidCoordsForGooglePreview,
    gpsCodePreview,
    existingCodeMode,
    existingCodeResolved,
  ])

  // Previsualizar googleCode según coordenadas capturadas (sin suffix).
  React.useEffect(() => {
    const lat = parseFloat(values.latY)
    const lng = parseFloat(values.longX)
    const hasValidCoords =
      !Number.isNaN(lat) &&
      !Number.isNaN(lng) &&
      lat !== 0 &&
      lng !== 0 &&
      lat >= -90 &&
      lat <= 90 &&
      lng >= -180 &&
      lng <= 180

    if (!hasValidCoords) {
      setRegisterGoogleCodePreview(null)
      setRegisterGoogleCodeLoading(false)
      setRegisterGoogleCodeError(false)
      return
    }

    setRegisterGoogleCodeLoading(true)
    setRegisterGoogleCodeError(false)
    setRegisterGoogleCodePreview(null)

    let cancelled = false
    const timer = setTimeout(async () => {
      if (cancelled) return

      try {
        const preview = await getGoogleCodePreviewByCoords({ lat, lng })
        if (cancelled) return
        setRegisterGoogleCodePreview(preview || null)
      } catch (_) {
        if (!cancelled) {
          setRegisterGoogleCodePreview(null)
          setRegisterGoogleCodeError(true)
        }
      } finally {
        if (!cancelled) setRegisterGoogleCodeLoading(false)
      }
    }, 500)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [values.latY, values.longX])

  // Auto-fetch elevation when coordinates change (blocked in edit mode unless explicitly requested)
  React.useEffect(() => {
    if (editMode && !recalculateRequested) return

    const lat = parseFloat(values.latY)
    const lng = parseFloat(values.longX)

    if (isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0) return

    const coordKey = `${lat.toFixed(6)},${lng.toFixed(6)}`
    if (!recalculateRequested && lastFetchedCoords.current === coordKey) return
    lastFetchedCoords.current = coordKey

    let cancelled = false
    setElevationLoading(true)
    setElevationError(false)

    fetchElevation(lat, lng).then(elevation => {
      if (cancelled) return
      setElevationLoading(false)
      setRecalculateRequested(false)

      if (elevation != null) {
        setFieldValue('altitud', elevation)
        setElevationAutoFetched(true)
        setManualOverride(false)
        setElevationError(false)
      } else {
        setElevationAutoFetched(false)
        setElevationError(true)
      }
    })

    return () => {
      cancelled = true
    }
  }, [values.latY, values.longX, setFieldValue, editMode, recalculateRequested])

  const isFieldDisabled = elevationAutoFetched && !manualOverride

  const handleRecalculateElevation = () => {
    lastFetchedCoords.current = null
    setRecalculateRequested(true)
  }

  const handleRevertElevation = () => {
    if (originalAltitude.current != null) {
      setFieldValue('altitud', originalAltitude.current)
    }
    setElevationAutoFetched(false)
    setManualOverride(false)
    lastFetchedCoords.current = null
  }

  return (
    <Stack spacing={2.5}>
      {/* City / Tenant selectors */}
      {editMode && originalCantonSlug ? (
        /* ── Edit mode ── */
        <Stack spacing={0.75}>
          {/* Line 1: current assignment + action button */}
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              icon={<LocationCityIcon />}
              label={isChangingAssignment ? originalCantonName : currentCityName}
              variant="outlined"
              size="small"
              sx={{ fontWeight: 700, cursor: 'default' }}
            />
            <Chip
              icon={<BusinessIcon />}
              label={isChangingAssignment ? (originalTenantName || '—') : (selectedTenantName || 'Institución')}
              variant="outlined"
              color={!isChangingAssignment && selectedTenantIsGeneric ? 'warning' : 'default'}
              size="small"
              sx={{ cursor: 'default' }}
            />
            {!isChangingAssignment && (
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={<SwapHorizIcon />}
                onClick={() => setIsChangingAssignment(true)}
                sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.75rem', ml: 0.5 }}>
                Reasignar
              </Button>
            )}
          </Stack>

          {/* Line 2: new selectors — only when user chose to reassign */}
          {isChangingAssignment && (
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
              <SwapHorizIcon sx={{ fontSize: 16, color: 'warning.main', flexShrink: 0 }} />
              <Chip
                icon={<LocationCityIcon />}
                label={currentCityName}
                deleteIcon={<KeyboardArrowDownIcon />}
                onDelete={e => setCityAnchor(e.currentTarget)}
                onClick={e => setCityAnchor(e.currentTarget)}
                variant={cantonChanged ? 'filled' : 'outlined'}
                color={cantonChanged ? 'warning' : 'default'}
                size="small"
                sx={{ fontWeight: 700 }}
              />
              {tenants.length > 0 && (
                <Chip
                  icon={<BusinessIcon />}
                  label={selectedTenantName || 'Institución'}
                  deleteIcon={<KeyboardArrowDownIcon />}
                  onDelete={e => setTenantAnchor(e.currentTarget)}
                  onClick={e => setTenantAnchor(e.currentTarget)}
                  variant={tenantChanged ? 'filled' : 'outlined'}
                  color={
                    tenantChanged
                      ? 'warning'
                      : selectedTenantIsGeneric
                        ? 'warning'
                        : selectedTenant
                          ? 'primary'
                          : 'default'
                  }
                  size="small"
                />
              )}
              <IconButton
                size="small"
                onClick={handleCancelAssignmentChange}
                title="Cancelar reasignación"
                sx={{ color: 'text.secondary' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          )}
        </Stack>
      ) : (
        /* ── Register mode (or edit mode before initialization) ── */
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip
            icon={<LocationCityIcon />}
            label={currentCityName}
            deleteIcon={<KeyboardArrowDownIcon />}
            onDelete={e => setCityAnchor(e.currentTarget)}
            onClick={e => setCityAnchor(e.currentTarget)}
            variant="outlined"
            color="default"
            sx={{ fontWeight: 700, px: 0.5, height: 36 }}
          />
          {tenants.length > 0 && (
            <Chip
              icon={<BusinessIcon />}
              label={selectedTenantName || 'Institución'}
              deleteIcon={<KeyboardArrowDownIcon />}
              onDelete={e => setTenantAnchor(e.currentTarget)}
              onClick={e => setTenantAnchor(e.currentTarget)}
              variant="outlined"
              color={selectedTenantIsGeneric ? 'warning' : selectedTenant ? 'primary' : 'default'}
              sx={{ px: 0.5, height: 36 }}
            />
          )}
        </Stack>
      )}

      {/* Shared menus for canton and tenant */}
      <Menu
        anchorEl={cityAnchor}
        open={Boolean(cityAnchor)}
        onClose={() => setCityAnchor(null)}
        slotProps={{ paper: { sx: { maxHeight: 280, minWidth: 180 } } }}>
        {cities.map(city => (
          <MenuItem
            key={city.slug}
            selected={city.slug === activeCantonSlug}
            onClick={() => handleCityChange(city.slug)}>
            <ListItemIcon>
              {city.slug === activeCantonSlug ? (
                <CheckIcon fontSize="small" color="success" />
              ) : (
                <LocationCityIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText>{city.name}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
      {tenants.length > 0 && (
        <Menu
          anchorEl={tenantAnchor}
          open={Boolean(tenantAnchor)}
          onClose={() => setTenantAnchor(null)}
          slotProps={{ paper: { sx: { maxHeight: 280, minWidth: 200 } } }}>
          {tenants.map(tenant => (
            <MenuItem
              key={tenant.slug}
              selected={tenant.slug === selectedTenant}
              onClick={() => handleTenantChange(tenant.slug)}>
              <ListItemIcon>
                {tenant.slug === selectedTenant ? (
                  <CheckIcon fontSize="small" color="success" />
                ) : (
                  <BusinessIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText>{tenant.name}</ListItemText>
              {tenant.isGeneric && (
                <Chip label="Temporal" size="small" color="warning" sx={{ ml: 1, fontSize: 10, height: 18 }} />
              )}
            </MenuItem>
          ))}
        </Menu>
      )}

      {tenantAccessDenied && (
        <Alert severity="warning" variant="outlined">
          No tienes acceso a esta institución. El árbol no podrá guardarse en el tenant seleccionado.
        </Alert>
      )}

      {/* Backdrop for expanded map - click to collapse */}
      <Backdrop
        open={mapExpanded}
        onClick={() => dispatch(setMapExpanded(false))}
        sx={{ zIndex: 1200, backgroundColor: 'rgba(0,0,0,0.4)' }}
      />

      {/* Map */}
      <Box
        ref={mapBoxRef}
        sx={{
          ...(mapExpanded
            ? {
                position: 'fixed',
                top: `calc(max(${drawerMinHeight}, ${drawerHeight}) + 12px)`,
                left: 16,
                right: 16,
                bottom: 16,
                zIndex: 1201,
                height: 'auto',
                boxShadow: 12,
                borderColor: 'primary.main',
              }
            : {
                position: 'relative',
                zIndex: 'auto',
                height: isMobile ? '35vh' : '360px',
                boxShadow: 0,
                borderColor: 'divider',
              }),
          borderRadius: 2,
          overflow: 'hidden',
          border: 1,
          transition: 'all 0.3s ease-in-out',
        }}>
        {mapComponent}
      </Box>

      {/* Coordinates */}
      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          borderColor: coordinatesOutOfTenantBounds ? 'error.main' : 'divider',
          borderWidth: coordinatesOutOfTenantBounds ? 2 : 1,
        }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <SectionHeader icon={GpsFixedIcon} label="Coordenadas" />
            <Button
              size="small"
              variant="outlined"
              startIcon={<VisibilityIcon />}
              disabled={!hasValidManualCoords}
              onClick={handlePreviewLocation}
              sx={{ textTransform: 'none', borderRadius: 2 }}>
              Ver ubicación
            </Button>
          </Stack>
          <Grid container spacing={2}>
            {coordItems.map(item => {
              const val = parseFloat(values[item.name])
              const isEmpty = isNaN(val) || val === 0
              return (
                <ItemsContainer
                  key={item.name}
                  {...item}
                  editable
                  {...(isEmpty && { error: true, helperText: 'Obligatorio' })}
                />
              )
            })}
          </Grid>

          {coordinatesOutOfTenantBounds && (
            <Alert severity="error" variant="outlined" sx={{ mt: 2 }}>
              Este punto está fuera de los límites del tenant asignado
            </Alert>
          )}

          {/* Altitude / Elevation */}
          <Box sx={{ mt: 2, px: 1 }}>
            <Box sx={{ position: 'relative' }}>
              {elevationLoading && (
                <CircularProgress
                  size={16}
                  sx={{ position: 'absolute', top: 4, right: 0 }}
                />
              )}
              <SliderField
                name="altitud"
                label="Altitud"
                min={0}
                max={5000}
                step={10}
                value={Number(values.altitud) || 0}
                onChange={(name, val) => {
                  if (!isFieldDisabled) setFieldValue(name, val)
                }}
                icon={HikingIcon}
                unit=" msnm"
                disabled={isFieldDisabled}
              />
            </Box>

            {/* Edit mode: recorded altitude label + recalculate button */}
            {editMode && !elevationAutoFetched && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Altitud registrada
                </Typography>
                {!elevationLoading && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={handleRecalculateElevation}
                    sx={{ textTransform: 'none', fontWeight: 600, minHeight: 28 }}>
                    Recalcular
                  </Button>
                )}
              </Stack>
            )}

            {elevationAutoFetched && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={0.5}
                sx={{ mt: 0.5 }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="caption" color="success.main">
                    {editMode
                      ? 'Elevación recalculada (SRTM30m)'
                      : 'Elevación capturada automáticamente (SRTM30m)'}
                  </Typography>
                  {editMode && (
                    <Button
                      size="small"
                      variant="text"
                      color="warning"
                      startIcon={<RestoreIcon sx={{ fontSize: 14 }} />}
                      onClick={handleRevertElevation}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        minHeight: 24,
                        py: 0,
                        px: 0.5,
                        fontSize: '0.72rem',
                      }}>
                      Regresar
                    </Button>
                  )}
                </Stack>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={manualOverride}
                      onChange={(_, checked) => setManualOverride(checked)}
                    />
                  }
                  label={
                    <Typography variant="caption" color="text.secondary">
                      Manual
                    </Typography>
                  }
                  labelPlacement="start"
                  sx={{ mr: 0 }}
                />
              </Stack>
            )}

            {elevationError && (
              <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
                No se pudo obtener la elevación automáticamente. Ingrese el valor
                manualmente.
              </Alert>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Site Classification */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}>
            <SectionHeader icon={NatureIcon} label="Clasificación del Sitio" />
            {selectedZoneColor && (
              <Chip
                size="small"
                label={selectedZoneCategory || ''}
                sx={{
                  bgcolor: selectedZoneColor,
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  textTransform: 'capitalize',
                }}
              />
            )}
          </Stack>
          <Grid container spacing={2}>
            {zoneTypeItems.map(item => (
              <ItemsContainer key={item.name} {...item} editable />
            ))}
            {filteredSitePlaceItem && (
              <ItemsContainer
                {...filteredSitePlaceItem}
                editable
                {...(!values.sitePlace_ &&
                  values.typeSite_ && {
                    error: true,
                    helperText: 'Obligatorio',
                  })}
              />
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Location Details */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <SectionHeader icon={PlaceIcon} label="Ubicación Específica" />
          <Grid container spacing={2}>
            {siteItems.map(item => (
              <ItemsContainer key={item.name} {...item} x={12} s={12} m={12} editable />
            ))}
            {!usePlusCode && (
              <>
                {/* Acronym (narrow) + Section chips (remaining) */}
                {acronymItems.map(item => (
                  <ItemsContainer key={item.name} {...item} x={5} s={4} m={4} editable />
                ))}
                <Grid item xs={7} sm={8} md={8}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.75, display: 'block' }}>
                    Sección
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={0.75}
                    flexWrap="wrap"
                    useFlexGap
                    alignItems="center">
                    {existingSections.map(sec => (
                      <Chip
                        key={sec.codigo}
                        label={sec.codigo}
                        size="small"
                        color={
                          !addNewSection && values.section_ === sec.codigo
                            ? 'primary'
                            : 'default'
                        }
                        variant={
                          !addNewSection && values.section_ === sec.codigo
                            ? 'filled'
                            : 'outlined'
                        }
                        onClick={() => handleSectionSelect(sec.codigo)}
                        sx={{ fontWeight: 600, minWidth: 40 }}
                      />
                    ))}
                    {!existingSections.length && !addNewSection && (
                      <Chip
                        label="S1"
                        size="small"
                        color="primary"
                        variant="filled"
                        sx={{ fontWeight: 600, minWidth: 40 }}
                      />
                    )}
                    <Chip
                      icon={<AddIcon sx={{ fontSize: '14px !important' }} />}
                      label={addNewSection ? nextSection : 'Nueva'}
                      size="small"
                      color={addNewSection ? 'success' : 'default'}
                      variant={addNewSection ? 'filled' : 'outlined'}
                      onClick={() => setAddNewSection(!addNewSection)}
                      sx={{ fontWeight: 500 }}
                    />
                  </Stack>
                  {selectedSectionName && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}>
                      {selectedSectionName}
                    </Typography>
                  )}
                </Grid>
                {/* Section name — only when adding new + user clicks button */}
                {addNewSection && (
                  <Grid item xs={12}>
                    {!showSectionName ? (
                      <Button
                        size="small"
                        onClick={() => setShowSectionName(true)}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          px: 1,
                          py: 0.25,
                        }}>
                        + Agregar nombre a {nextSection}
                      </Button>
                    ) : (
                      <TextField
                        fullWidth
                        size="small"
                        variant="standard"
                        name="sectionName_"
                        label={`Nombre de ${nextSection}`}
                        placeholder={`Opcional — por defecto "${nextSection}"`}
                        value={values.sectionName_ || ''}
                        onChange={e => setFieldValue('sectionName_', e.target.value)}
                        autoFocus
                      />
                    )}
                  </Grid>
                )}
              </>
            )}
            {/* Referencia de ubicación */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                variant="standard"
                name="referenciaUbicacion_"
                label="Referencia de ubicación"
                placeholder="Ej: Frente a la gobernación, esquina noroeste"
                value={values.referenciaUbicacion_ || ''}
                onChange={e => setFieldValue('referenciaUbicacion_', e.target.value)}
                multiline
                minRows={1}
                maxRows={3}
                error={!values.referenciaUbicacion_?.trim()}
                helperText={
                  !values.referenciaUbicacion_?.trim()
                    ? 'Obligatorio'
                    : 'Descripción textual que ayude a localizar el árbol'
                }
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Code Identification */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <SectionHeader icon={QrCodeIcon} label="Código de Identificación" />

          {editMode ? (
            /* ── Edit mode: opciones con URL resultante visible ── */
            <Box>
              {/* Código actual */}
              <Box
                sx={{
                  mb: 2,
                  p: 1.25,
                  borderRadius: 1.5,
                  bgcolor: 'action.hover',
                  border: 1,
                  borderColor: 'divider',
                }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 0.75, display: 'block' }}>
                  Código actual registrado
                </Typography>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap>
                  <Chip
                    label={existingCodeResolved || 'Sin código previo'}
                    sx={{
                      fontFamily: 'monospace',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      letterSpacing: '0.04em',
                    }}
                    variant="filled"
                  />
                  {existingCodeMode === 'STRUCTURED' && (
                    <Chip
                      label="Estructurado nuevo"
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {existingCodeMode === 'STRUCTURED_LEGACY' && (
                    <Chip
                      label="Estructurado antiguo"
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  )}
                  {existingCodeMode === 'MANUAL' && (
                    <Chip
                      label="Código manual"
                      size="small"
                      color="default"
                      variant="outlined"
                    />
                  )}
                  {existingCodeMode === 'PLUS_CODE' && (
                    <Chip
                      label="Código GPS (Plus Code)"
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Box>

              {/* Opciones con URL resultante */}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1.25, display: 'block', fontWeight: 500 }}>
                ¿Qué código usará este árbol?
              </Typography>
              <RadioGroup
                value={codeChangeMode}
                onChange={(_, val) => setCodeChangeMode(val)}
                sx={{ gap: 0.75 }}>
                {/* Opción 1: Conservar código antiguo */}
                {showKeepManualOption && (
                  <Paper
                    variant="outlined"
                    onClick={() => setCodeChangeMode('keep')}
                    sx={{
                      p: 1.25,
                      borderRadius: 1.5,
                      borderColor: codeChangeMode === 'keep' ? 'warning.main' : 'divider',
                      bgcolor:
                        codeChangeMode === 'keep'
                          ? 'rgba(237,108,2,0.04)'
                          : 'transparent',
                      cursor: 'pointer',
                      '&:hover': { borderColor: 'warning.light' },
                    }}>
                    <FormControlLabel
                      value="keep"
                      control={<Radio size="small" sx={{ py: 0 }} color="warning" />}
                      label={
                        <Stack spacing={0.4}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="body2" fontWeight={600}>
                              Conservar código manual
                            </Typography>
                            <Chip
                              label={existingCodeResolved}
                              size="small"
                              sx={{
                                fontFamily: 'monospace',
                                fontWeight: 700,
                                fontSize: '0.75rem',
                              }}
                              variant="outlined"
                              color="warning"
                            />
                          </Stack>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: 'monospace' }}>
                            URL: /{currentCity}/{existingCodeResolved}
                          </Typography>
                        </Stack>
                      }
                      sx={{ m: 0, width: '100%', alignItems: 'flex-start' }}
                    />
                  </Paper>
                )}

                {/* Opción 2: Plus Code (GPS) */}
                <Paper
                  variant="outlined"
                  onClick={() => setCodeChangeMode('plus_code')}
                  sx={{
                    p: 1.25,
                    borderRadius: 1.5,
                    borderColor: codeChangeMode === 'plus_code' ? 'info.main' : 'divider',
                    bgcolor:
                      codeChangeMode === 'plus_code'
                        ? 'rgba(2,136,209,0.04)'
                        : 'transparent',
                    cursor: 'pointer',
                    '&:hover': { borderColor: 'info.light' },
                  }}>
                  <FormControlLabel
                    value="plus_code"
                    control={<Radio size="small" sx={{ py: 0 }} color="info" />}
                    label={
                      <Stack spacing={0.4}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body2" fontWeight={600}>
                            Código GPS (Google Plus Code)
                          </Typography>
                          {registerGoogleCodeLoading && !plusCodeOptionPreview ? (
                            <CircularProgress size={14} />
                          ) : null}
                          {plusCodeOptionPreview ? (
                            <Chip
                              label={plusCodeOptionPreview}
                              size="small"
                              sx={{
                                width: 'fit-content',
                                fontFamily: 'monospace',
                                fontWeight: 700,
                                fontSize: '0.75rem',
                              }}
                              variant="outlined"
                              color="info"
                            />
                          ) : null}
                        </Stack>

                        <Typography variant="caption" color="text.secondary">
                          Se genera desde las coordenadas — reemplaza la URL actual
                        </Typography>
                        <Typography
                          variant="caption"
                          color="info.main"
                          sx={{ fontFamily: 'monospace' }}>
                          URL: /{currentCity}/{plusCodeOptionPreview || 'XXXX+XX'}
                          {!plusCodeOptionPreview ? ' (calculado al guardar)' : ''}
                        </Typography>
                        {registerGoogleCodeError && !registerGoogleCodeLoading && (
                          <Typography variant="caption" color="error.main">
                            No se pudo recalcular el código GPS con las coordenadas actuales.
                          </Typography>
                        )}
                      </Stack>
                    }
                    sx={{ m: 0, width: '100%', alignItems: 'flex-start' }}
                  />
                </Paper>

                {/* Opción 3: Código estructurado nuevo */}
                <Paper
                  variant="outlined"
                  onClick={() => setCodeChangeMode('update_structured')}
                  sx={{
                    p: 1.25,
                    borderRadius: 1.5,
                    borderColor:
                      codeChangeMode === 'update_structured' ? 'primary.main' : 'divider',
                    bgcolor:
                      codeChangeMode === 'update_structured'
                        ? 'rgba(25,118,210,0.04)'
                        : 'transparent',
                    cursor: 'pointer',
                    '&:hover': { borderColor: 'primary.light' },
                  }}>
                  <FormControlLabel
                    value="update_structured"
                    control={<Radio size="small" sx={{ py: 0 }} />}
                    label={
                      <Stack spacing={0.4}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body2" fontWeight={600}>
                            Código estructurado nuevo
                          </Typography>
                          {codePreview ? (
                            <Chip
                              label={codePreview}
                              size="small"
                              sx={{
                                fontFamily: 'monospace',
                                fontWeight: 700,
                                fontSize: '0.75rem',
                              }}
                              variant="outlined"
                            />
                          ) : null}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          <Box
                            component="span"
                            sx={{
                              color: structuredCodeStatus.missing.zoneType
                                ? 'error.main'
                                : 'text.secondary',
                              fontWeight: structuredCodeStatus.missing.zoneType ? 700 : 400,
                            }}>
                            Eje verde
                          </Box>{' '}
                          ·{' '}
                          <Box
                            component="span"
                            sx={{
                              color: structuredCodeStatus.missing.sitePlace
                                ? 'error.main'
                                : 'text.secondary',
                              fontWeight: structuredCodeStatus.missing.sitePlace ? 700 : 400,
                            }}>
                            Emplazamiento
                          </Box>{' '}
                          ·{' '}
                          <Box
                            component="span"
                            sx={{
                              color: structuredCodeStatus.missing.acronym
                                ? 'error.main'
                                : 'text.secondary',
                              fontWeight: structuredCodeStatus.missing.acronym ? 700 : 400,
                            }}>
                            Acrónimo de Ubicación
                          </Box>{' '}
                          ·{' '}
                          <Box
                            component="span"
                            sx={{
                              color: structuredCodeStatus.missing.section
                                ? 'error.main'
                                : 'text.secondary',
                              fontWeight: structuredCodeStatus.missing.section ? 700 : 400,
                            }}>
                            Sección
                          </Box>
                        </Typography>
                        {codePreview ? (
                          <Typography
                            variant="caption"
                            color="primary.main"
                            sx={{ fontFamily: 'monospace' }}>
                            URL: /{currentCity}/{codePreview}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="warning.main">
                            Complete los campos de clasificación para ver la URL
                            resultante
                          </Typography>
                        )}
                      </Stack>
                    }
                    sx={{ m: 0, width: '100%', alignItems: 'flex-start' }}
                  />
                </Paper>
              </RadioGroup>

              {/* Configuración avanzada: código manual */}
              <Box sx={{ mt: 1.5 }}>
                <CollapsibleAdvanced>
                  <TextField
                    fullWidth
                    size="small"
                    name="codigoManual"
                    label="Código manual"
                    placeholder="Ej: CUNL-ESPECIAL-001"
                    value={values.codigoManual || ''}
                    onChange={e => setFieldValue('codigoManual', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <QrCodeIcon sx={{ fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                    helperText={
                      values.codigoManual
                        ? `URL resultante: /${currentCity}/${values.codigoManual}`
                        : 'Ingrese un código personalizado para la URL'
                    }
                  />
                </CollapsibleAdvanced>
              </Box>
            </Box>
          ) : (
            /* ── Modo creación: opciones con radio ── */
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1.25, display: 'block', fontWeight: 500 }}>
                ¿Qué código usará este árbol?
              </Typography>

              <RadioGroup
                value={usePlusCode ? 'plus_code' : 'update_structured'}
                onChange={(_, val) =>
                  setFieldValue('modoCodigoPlusCode', val === 'plus_code')
                }
                sx={{ gap: 0.75 }}>
                <Paper
                  variant="outlined"
                  onClick={() => setFieldValue('modoCodigoPlusCode', true)}
                  sx={{
                    p: 1.25,
                    borderRadius: 1.5,
                    borderColor: usePlusCode ? 'info.main' : 'divider',
                    bgcolor: usePlusCode ? 'rgba(2,136,209,0.04)' : 'transparent',
                    cursor: 'pointer',
                    '&:hover': { borderColor: 'info.light' },
                  }}>
                  <FormControlLabel
                    value="plus_code"
                    control={<Radio size="small" sx={{ py: 0 }} color="info" />}
                    label={
                      <Stack spacing={0.4}>
                        {plusCodeOptionPreview ? (
                          <PlusCodePreview
                            googlePlusCode={plusCodeOptionPreview}
                            citySlug={currentCity}
                          />
                        ) : null}
                        {!plusCodeOptionPreview && (
                          <>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Typography variant="body2" fontWeight={600}>
                                Código GPS (Google Plus Code)
                              </Typography>
                              <Chip
                                label="Automático"
                                size="small"
                                color="info"
                                variant="outlined"
                              />
                              {registerGoogleCodeLoading ? (
                                <CircularProgress size={14} />
                              ) : null}
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                              {registerGoogleCodeLoading
                                ? 'Consultando previsualización desde backend...'
                                : registerGoogleCodeError
                                  ? 'No se pudo recalcular por ahora; se generará al guardar'
                                  : 'Se genera automáticamente desde las coordenadas'}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="info.main"
                              sx={{ fontFamily: 'monospace' }}>
                              URL: /{currentCity}/XXXX+XX (calculado al guardar)
                            </Typography>
                          </>
                        )}
                      </Stack>
                    }
                    sx={{ m: 0, width: '100%', alignItems: 'flex-start' }}
                  />
                </Paper>

                <Paper
                  variant="outlined"
                  onClick={() => setFieldValue('modoCodigoPlusCode', false)}
                  sx={{
                    p: 1.25,
                    borderRadius: 1.5,
                    borderColor: !usePlusCode ? 'primary.main' : 'divider',
                    bgcolor: !usePlusCode ? 'rgba(25,118,210,0.04)' : 'transparent',
                    cursor: 'pointer',
                    '&:hover': { borderColor: 'primary.light' },
                  }}>
                  <FormControlLabel
                    value="update_structured"
                    control={<Radio size="small" sx={{ py: 0 }} />}
                    label={
                      <Stack spacing={0.4}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body2" fontWeight={600}>
                            Código estructurado nuevo
                          </Typography>
                          {codePreview ? (
                            <Chip
                              label={codePreview}
                              size="small"
                              sx={{
                                fontFamily: 'monospace',
                                fontWeight: 700,
                                fontSize: '0.75rem',
                              }}
                              variant="outlined"
                            />
                          ) : null}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          <Box
                            component="span"
                            sx={{
                              color: structuredCodeStatus.missing.zoneType
                                ? 'error.main'
                                : 'text.secondary',
                              fontWeight: structuredCodeStatus.missing.zoneType ? 700 : 400,
                            }}>
                            Eje verde
                          </Box>{' '}
                          ·{' '}
                          <Box
                            component="span"
                            sx={{
                              color: structuredCodeStatus.missing.sitePlace
                                ? 'error.main'
                                : 'text.secondary',
                              fontWeight: structuredCodeStatus.missing.sitePlace ? 700 : 400,
                            }}>
                            Emplazamiento
                          </Box>{' '}
                          ·{' '}
                          <Box
                            component="span"
                            sx={{
                              color: structuredCodeStatus.missing.acronym
                                ? 'error.main'
                                : 'text.secondary',
                              fontWeight: structuredCodeStatus.missing.acronym ? 700 : 400,
                            }}>
                            Acrónimo de Ubicación
                          </Box>{' '}
                          ·{' '}
                          <Box
                            component="span"
                            sx={{
                              color: structuredCodeStatus.missing.section
                                ? 'error.main'
                                : 'text.secondary',
                              fontWeight: structuredCodeStatus.missing.section ? 700 : 400,
                            }}>
                            Sección
                          </Box>
                        </Typography>
                        {codePreview ? (
                          <Typography
                            variant="caption"
                            color="primary.main"
                            sx={{ fontFamily: 'monospace' }}>
                            URL: /{currentCity}/{codePreview}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="warning.main">
                            Complete los campos de clasificación para ver la URL resultante
                          </Typography>
                        )}
                      </Stack>
                    }
                    sx={{ m: 0, width: '100%', alignItems: 'flex-start' }}
                  />
                </Paper>
              </RadioGroup>

              {!usePlusCode && (
                <Box sx={{ mt: 1.5 }}>
                  <CollapsibleAdvanced>
                    <TextField
                      fullWidth
                      size="small"
                      name="codigoManual"
                      label="Código manual (opcional)"
                      placeholder="Ingrese un código personalizado"
                      value={values.codigoManual || ''}
                      onChange={e => setFieldValue('codigoManual', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <QrCodeIcon sx={{ fontSize: 18 }} />
                          </InputAdornment>
                        ),
                      }}
                      helperText={
                        values.codigoManual
                          ? `URL resultante: /${currentCity}/${values.codigoManual}`
                          : 'Solo si necesita un código diferente al estructurado'
                      }
                    />
                  </CollapsibleAdvanced>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Stack>
  )
}

export default LocationStep
